import { useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { KitInputProvider, Menu, type KitInput } from "../../ui/kit/index.ts";
import { cssPerLogical } from "../../ui/kit/measure.ts";
import { say } from "../../words.ts";
import { sameName } from "../party/members.ts";
import type { PartyMenuAt, PartySource } from "../party/store.ts";
import type { ChatSource } from "../chat/store.ts";
import { playerMenuEntries, type PlayerChoice } from "./entries.ts";

/**
 * The player menu (ADR-101's note of 2026-10-16; the design's `PlayerMenu` card in
 * `ui_kits/surfaces/SocialSurfaces.jsx`, a visual reference): the kit's `Menu`, headed with the character's name,
 * opening at a point in client CSS pixels and kept 4 logical pixels inside the screen. One menu serves a player's
 * body, nameplate, name in chat and row of the party frame; its entries are `playerMenuEntries`'. It holds the
 * keyboard while it is open and Escape closes it, as the kit's menu does; it is opened on purpose.
 */

/**
 * The menu's least width in logical pixels, the design's player menu's.
 */
export const PARTY_MENU_WIDTH = 120;

/**
 * How far inside the screen's edge the menu is kept, in logical pixels.
 */
export const PARTY_MENU_EDGE = 4;

/**
 * The open menu, fixed at its point and moved back inside the screen once it has a size.
 *
 * @param props The point it opens at (`at`), its `heading`, its entries, `onSelect` with the entry chosen and
 *   `onClose`, which Escape, Tab and a choice call.
 * @returns The menu.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
function PlacedMenu({ at, heading, entries, onSelect, onClose }: { readonly at: PartyMenuAt; readonly heading: string; readonly entries: ReturnType<typeof playerMenuEntries>; readonly onSelect: (choice: PlayerChoice) => void; readonly onClose: () => void }): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState({ x: at.x, y: at.y });
  useLayoutEffect(() => {
    const box = host.current;
    if (box === null) {
      return;
    }
    const edge = (cssPerLogical(box) ?? 1) * PARTY_MENU_EDGE;
    const x = Math.max(edge, Math.min(at.x, window.innerWidth - edge - box.offsetWidth));
    const y = Math.max(edge, Math.min(at.y, window.innerHeight - edge - box.offsetHeight));
    setPlace((before) => (before.x === x && before.y === y ? before : { x, y }));
  }, [at.x, at.y]);
  return (
    <div ref={host} className="ot-party-menu" data-party="menu" data-player-menu="menu" style={{ left: `${place.x}px`, top: `${place.y}px` }}>
      <Menu label={say("party.menu.label")} heading={heading} minWidth={PARTY_MENU_WIDTH} items={entries} onSelect={onSelect} onClose={onClose} />
    </div>
  );
}

/**
 * What a choice does besides asking the party: starting a whisper and blocking or unblocking a family.
 */
export interface PlayerMenuActions {
  /** Starts a whisper to a character: the chat input holds `/w First Family ` and takes the focus. */
  readonly whisper: (name: { readonly firstName: string; readonly familyName: string }) => void;
  /** Blocks a family as `/block` does. */
  readonly block: (familyName: string) => void;
  /** Unblocks a family as `/unblock` does. */
  readonly unblock: (familyName: string) => void;
}

/**
 * The menu layer: the menu the party store holds open, if it has anything to offer, and what each entry asks.
 *
 * @param props The `party` it reads and asks through (its snapshot says whether the viewer's invitations are held,
 *   in combat or dead, and changes live while the menu is open), the `chat` whose block list it reads, the `actions` for the entries that are not the party's, and the page's input.
 * @returns The menu, or null while none is open.
 * @throws Error when mounted outside a `KitInputProvider` and given no input.
 */
export function PlayerMenu({ party, chat, actions, input }: { readonly party: PartySource; readonly chat: Pick<ChatSource, "subscribe" | "getSnapshot">; readonly actions: PlayerMenuActions; readonly input: KitInput }): ReactElement | null {
  const snapshot = useSyncExternalStore(party.subscribe, party.getSnapshot, party.getSnapshot);
  const blocks = useSyncExternalStore(chat.subscribe, () => chat.getSnapshot().blocks, () => chat.getSnapshot().blocks);
  const at = snapshot.menu;
  if (at === null) {
    return null;
  }
  const entries = playerMenuEntries({ party: snapshot, blocks, inCombat: snapshot.held }, at);
  if (entries.length === 0) {
    return null;
  }
  const seat = snapshot.members.find((member) => sameName(member, at));
  const choose = (choice: PlayerChoice): void => {
    party.closeMenu();
    const names = { firstName: at.firstName, familyName: at.familyName };
    switch (choice) {
      case "whisper":
        actions.whisper(names);
        return;
      case "block":
        actions.block(at.familyName);
        return;
      case "unblock":
        actions.unblock(at.familyName);
        return;
      case "invite":
        party.request({ kind: "partyInvite", ...names });
        return;
      case "leave":
        party.request({ kind: "partyLeave" });
        return;
      case "remove":
        party.request({ kind: "partyRemove", ...names });
        return;
      case "promote":
        party.request({ kind: "partyPromote", ...names });
        return;
      case "grant":
        party.request({ kind: "partyGrant", ...names, mayInvite: seat?.mayInvite !== true });
        return;
      case "trade":
      case "card":
      case "report":
        return;
    }
  };
  return (
    <KitInputProvider input={input}>
      <PlacedMenu key={`${at.firstName}:${at.familyName}:${at.x}:${at.y}`} at={at} heading={`${at.firstName} ${at.familyName}`.trim()} entries={entries} onSelect={choose} onClose={party.closeMenu} />
    </KitInputProvider>
  );
}
